import { useEffect, useState } from "react";
import useApi from "../Api";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Grid2,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import minecraft from "../minecraft.svg";
import forge from "../forge.png";
import InvisibleInput from "./InvisibleInput";
import { ModdingData, VersionData } from "../types/version";

type VersionProps = {
  version: VersionData;
};

type PutVersionsData = {
  uuid: string;
  url: string;
};

const Version: React.FC<VersionProps> = ({ version }) => {
  return (
    <Paper sx={{ padding: "10px 20px", marginTop: "20px" }}>
      <Grid2 container>
        <Grid2 size={8}>
          <Typography variant="h4" sx={{ paddingBottom: "10px" }}>
            {version.name}
            <Tooltip title={`Mineraft version: ${version.minecraftVersion}`}>
              <Chip
                avatar={<Avatar alt="Minecraft" src={minecraft} />}
                label={version.minecraftVersion}
                variant="outlined"
                sx={{ marginLeft: "20px" }}
              />
            </Tooltip>
            {version.modding?.modLoader === "forge" && (
              <Tooltip
                title={`Forge version: ${version.modding.modLoaderVersion}`}
              >
                <Chip
                  avatar={<Avatar alt="Forge" src={forge} />}
                  label={version.modding.modLoaderVersion}
                  variant="outlined"
                  sx={{ marginLeft: "20px" }}
                />
              </Tooltip>
            )}
          </Typography>
        </Grid2>
      </Grid2>
    </Paper>
  );
};

type MinecraftVersionProps = {
  minecraftVersion: string;
  vanilla: VersionData | null;
  modded: VersionData[];
};

const ModdedVersion: React.FC<{ modding: ModdingData }> = ({
  modding: { modLoader, modLoaderVersion },
}) => {
  switch (modLoader) {
    case "forge":
      return (
        <Tooltip title={`Forge version: ${modLoaderVersion}`}>
          <Chip
            avatar={<Avatar alt="Forge" src={forge} />}
            label={modLoaderVersion}
            variant="outlined"
            sx={{ marginLeft: "20px" }}
          />
        </Tooltip>
      );

    default:
      return (
        <span>
          {modLoader}-{modLoaderVersion}
        </span>
      );
  }
};

const MinecraftVersion: React.FC<MinecraftVersionProps> = ({
  minecraftVersion,
  vanilla,
  modded,
}) => {
  return (
    <Box sx={{ marginTop: "10px" }}>
      <Chip
        avatar={<Avatar alt="Minecraft" src={minecraft} />}
        label={minecraftVersion}
        variant="outlined"
        sx={{ marginLeft: "20px" }}
      />
      {modded.length !== 0 && (
        <Box sx={{ paddingTop: "10px", paddingLeft: "40px" }}>
          {modded.map((version) => (
            <ModdedVersion modding={version.modding!} />
          ))}
        </Box>
      )}
    </Box>
  );
};

const Versions: React.FC = () => {
  const { get, put, patch } = useApi();
  const [versions, setVersions] = useState<VersionData[]>([]);

  useEffect(() => {
    get<{ versions: VersionData[] }>("versions").then((data) =>
      setVersions(data.versions)
    );
  }, [get]);

  const minecraftVersions = new Map<string, MinecraftVersionProps>();

  versions.forEach((version) => {
    const { minecraftVersion } = version;
    let props = minecraftVersions.get(minecraftVersion);
    if (!props) {
      props = {
        minecraftVersion,
        vanilla: null,
        modded: [],
      };
      minecraftVersions.set(minecraftVersion, props);
    }
    if (version.modding) {
      props.modded.push(version);
    } else {
      props.vanilla = version;
    }
  });

  async function uploadInstaller(file: File) {
    const filename = file.name;

    const { uuid, url } = await put<PutVersionsData>("versions", { filename });

    const response = await fetch(url, {
      method: "PUT",
      headers: { "content-type": "application/octet-stream" },
      body: file,
    });

    if (!response.ok) {
      alert("Upload failed!");
      return;
    }

    await patch("versions", { uuid, filename });
  }

  return (
    <main>
      {[...minecraftVersions.entries()].map(([minecraftVersion, props]) => (
        <MinecraftVersion key={minecraftVersion} {...props} />
      ))}
      <Box
        sx={{ display: "flex", justifyContent: "center", paddingTop: "20px" }}
      >
        <Button
          component="label"
          role={undefined}
          variant="contained"
          tabIndex={-1}
          startIcon={<CloudUploadIcon />}
        >
          Upload Installer
          <InvisibleInput
            type="file"
            accept=".jar"
            onChange={(event) => uploadInstaller(event.target.files![0])}
          />
        </Button>
      </Box>
    </main>
  );
};

export default Versions;
